import { useMemo, useState, useRef, useEffect } from 'react'
import linkStyles from '../AppNavigation.pcss'
import { useDispatch, useSelector } from 'react-redux'
import { planToProfilesArraySelector, planToProfilesSelector } from 'services/plan/selectors'
import { AppNavLink } from '../AppNavLink'
import Collapse from '@mui/material/Collapse'
import SidebarProfileSelector from './SidebarProfileSelector'
import { postsUISelector, sidebarActiveViewSelector } from 'services/post/selectors'
import { connectedDestinationsSelector } from 'services/destinations'
import { useMatch, useNavigate } from 'react-router'
import { setActiveSidebarView, toggleSidebarProfilesExpanded } from 'services/post'
import { FormattedMessage, useIntl } from 'react-intl'
import { CreateUpdatePlanPopup } from './CreateUpdatePlanPopup'
import { createPlan, reorderPlans, updatePlan } from 'services/plan/actions'
import { StoreThunkDispatch } from 'store/state'
import styles from './PlansNavigation.pcss'
import ArrowIcon from '@mui/icons-material/KeyboardArrowRight'
import { message } from 'services/snackbar'
import { PostDestination, arrayToRecord, reorder } from 'shared'
import { noop } from 'utils/noop'
import Tooltip from '@mui/material/Tooltip'
import EditIcon from '@mui/icons-material/Edit'
import { mdiInformationOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { DndContext, useDroppable, useSensors, useSensor, PointerSensor } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { Observable } from 'rxjs/Observable'
import { Subject } from 'rxjs/Subject'
import { catchError } from 'rxjs/operators/catchError'

export function PlansNavigation() {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const navigate = useNavigate()
  const match = useMatch('/plans/:planId')
  const postSelectorView = useSelector(sidebarActiveViewSelector)
  const plansInfo = useSelector(planToProfilesSelector)
  const plansArray = useSelector(planToProfilesArraySelector)
  const plansMap = arrayToRecord(plansArray, 'id')
  const idsOrdered = useMemo(() => plansArray.map(p => p.id), [plansArray])
  const ui = useSelector(postsUISelector)
  const profiles = useSelector(connectedDestinationsSelector)
  const profilesByPpid = arrayToRecord(Object.values(profiles), 'ppid')
  const planProfilesIds: string[] = (plansInfo[match?.params.planId || '']?.ppids || [])
    .map(ppid => profilesByPpid[ppid]?.id)
    .filter(Boolean)
  const ppidToPlanId = Object.keys(plansInfo).reduce((map: Record<string, string>, planId) => {
    plansInfo[planId].ppids.forEach(ppid => {
      map[ppid] = planId
    })
    return map
  }, {})
  const [createPlanPopupOpen, setCreatePlanPopupOpen] = useState<boolean>(false)
  const [editPlan, setEditPlan] = useState<PlanBase | null>(null)
  const [order, setOrder] = useState<string[]>(idsOrdered)
  const orderRef = useRef<string[]>(idsOrdered)
  const { setNodeRef: setDroppableNodeRef } = useDroppable({ id: 'plan-droppable' })
  const reorder$ = useRef<Subject<string[]>>(undefined)

  useEffect(() => {
    setOrder(idsOrdered)
    orderRef.current = idsOrdered
  }, [idsOrdered])

  useEffect(() => {
    reorder$.current = new Subject()
    reorder$.current.switchMap((ids) => {
      return Observable.fromPromise(dispatch(reorderPlans(ids)).unwrap()).pipe(catchError((error) => {
        dispatch(message(intl.formatMessage({ id: 'notifications.generic.update-failed' }), 'error'))
        return Observable.of({ error })
      }))
    })
      .subscribe((response: any) => {
        if (response.error) {
          // revert to previous ordering
          setOrder(orderRef.current)
        } else {
          setOrder(response)
          orderRef.current = response
        }
      })
  }, [dispatch, intl])

  const toggleProfilesVisible = () => {
    dispatch(toggleSidebarProfilesExpanded())
  }

  const openCreatePlanPopup = () => {
    setCreatePlanPopupOpen(true)
  }

  const closePopup = () => {
    setCreatePlanPopupOpen(false)
    setEditPlan(null)
  }

  const setProfileSelectorView = (view: 'grid' | 'list' | 'network') => {
    dispatch(setActiveSidebarView(view))
  }

  const onCreatePlan = (ppids: string[], name: string, copyFrom?: string) => {
    return dispatch(createPlan({ ppids, copyFromPlanId: copyFrom, name }))
      .unwrap()
      .then((plan: any) => {
        setTimeout(() => navigate('/plans/' + plan.id))
      })
      .catch(() => {
        dispatch(message(intl.formatMessage({ id: 'errors.generic' })))
      })
  }

  const onUpdatePlan = (planId: string, name: string, addPpids: string[], copyFrom?: string) => {
    return dispatch(updatePlan({ planId, name, addPpids, copyFrom }))
      .unwrap()
      .then(() => {
        dispatch(message(intl.formatMessage({ id: 'app.nav.publishing-plans.update-success' })))
      })
      .catch(() => {
        dispatch(message(intl.formatMessage({ id: 'errors.generic' })))
      })
  }

  const onProfileClick = ({ ppid }: PostDestination) => {
    navigate(`/plans/${ppidToPlanId[ppid]}`)
  }

  const onEdit = (p: PlanBase) => {
    setEditPlan(p)
    setCreatePlanPopupOpen(true)
  }

  const onDragEnd = ({ active, over }: any) => {
    if (active.id !== over.id) {
      const oldIndex = order.indexOf(active.id)
      const newIndex = order.indexOf(over.id)
      const reordered = reorder(order, oldIndex, newIndex)
      setOrder(reordered)
      reorder$.current?.next(reordered)
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  )

  return (
    <div className={linkStyles['plans-nav']} data-testid="plans-nav">
      <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext items={order} strategy={rectSortingStrategy} modifiers={[restrictToParentElement]}>
          <div ref={setDroppableNodeRef}>
            {order.map((id) => {
              const plan = plansMap[id]
              if (!plan) {
                return null
              }

              return (
                <PlanLink plan={plan} key={id} onEdit={onEdit} />
              )
            })}
          </div>
        </SortableContext>
      </DndContext>
      <div className={`${linkStyles['child-link']} ${linkStyles['link-action']} ${styles['link-create']}`} onClick={openCreatePlanPopup}>
        + <FormattedMessage id="app.nav.publishing-plans.create" />
      </div>
      <div>
        <div className={styles.title}>
          <FormattedMessage id="general.labels.profiles" />
          <span className={`${styles['btn-toggle']} ${ui.sidebarProfilesExpanded ? styles.open : ''}`} onClick={toggleProfilesVisible}>
            <ArrowIcon />
          </span>
        </div>
        <Collapse in={ui.sidebarProfilesExpanded}>
          <SidebarProfileSelector
            profiles={profiles}
            activeView={postSelectorView}
            selectedProfileIds={planProfilesIds}
            contentClassName={styles['profiles-box']}
            multipleProfileSelection
            onProfileClick={onProfileClick}
            onSelectedProfilesChange={noop}
            setActiveView={setProfileSelectorView}
          />
        </Collapse>
      </div>

      {createPlanPopupOpen && (
        <CreateUpdatePlanPopup
          plan={editPlan}
          plans={plansInfo}
          onCreate={onCreatePlan}
          onUpdate={onUpdatePlan}
          onClose={closePopup}
        />
      )}
    </div>
  )
}

type PlanBase = { id: string, name: string, ppids: string[] }

function PlanLink({ plan, onEdit }: { plan: PlanBase, onEdit: (p: PlanBase) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: plan.id })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    pointerEvents: isDragging ? 'none' : 'all'
  } as any

  const edit = () => {
    onEdit(plan)
  }

  return (
    <div style={style} ref={setNodeRef}>
      <AppNavLink
        key={plan.id}
        to={`/plans/${plan.id}`}
        className={linkStyles['plan-link']}
        label={(
          <div className={linkStyles['link-label-box']} {...attributes} {...listeners}>
            <Tooltip title={<FormattedMessage id="actions.edit" />} placement="top">
              <EditIcon data-testid="btn-edit" className={linkStyles['btn-edit']} onClick={edit} />
            </Tooltip>
            <div className="text-ellipsis">{plan.name}</div>
          </div>
        )}
      />
    </div>
  )
}
